import React, { useRef, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  CmpAdvertisingOptionFieldsFragment,
  useCreateCmpAdvertisingOptionMutation,
  useDeleteCmpAdvertisingOptionMutation,
  useUpdateCmpAdvertisingOptionMutation,
} from "../../../../graphql/types";
import {
  centsToCurrencyValue,
  formatCurrencyValueOnBlur,
  sanitizeCurrencyValue,
} from "../../../common/dashboard/currencyUnits";
import { formatCurrency } from "../invoiceFormatting";
import { cpaGold, cpaGoldDark, InvoiceSection } from "../InvoiceMockComponents";
import {
  InvoiceDialogCloseButton,
  invoiceDialogPaperSx,
  invoiceFieldSx,
  invoiceModalActionSx,
} from "../InvoiceDesignSystem";
import { parseCurrencyToCents } from "./createInvoiceHelpers";
import { CALLER_HANDLED_USER_ERROR_CONTEXT } from "../../../../utils/apolloErrorPolicy";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
} from "../../../../utils/userFacingError";

// Save and delete errors show in the open pop-up, not the full-page error.
const advertisingOptionMutationOptions = {
  context: CALLER_HANDLED_USER_ERROR_CONTEXT,
};

const DESCRIPTION_MAX_LENGTH = 50;

type EditorState = {
  advertisingOptionId: string | null;
  code: string;
  name: string;
  description: string;
  priceInput: string;
};

// POL-019 (B5): per-field dialog validation (code/name required, price > 0).
type EditorFieldErrors = {
  code?: string;
  name?: string;
  price?: string;
};

const emptyEditorState: EditorState = {
  advertisingOptionId: null,
  code: "",
  name: "",
  description: "",
  priceInput: "",
};

const toEditorState = (
  option: CmpAdvertisingOptionFieldsFragment,
): EditorState => ({
  advertisingOptionId: option.id,
  code: option.code,
  name: option.name,
  description: option.description || "",
  // POL-005 (B5): canonical 2-decimal dollars string ("300.00"), matching the
  // blur-canonicalized entry shape.
  priceInput: centsToCurrencyValue(option.priceInCents),
});

export const AdvertisingOptionsManager = ({
  advertisingOptions,
  chamberId,
  isCmpInvoiceModuleTestData,
  loading,
  onChanged,
}: {
  advertisingOptions: CmpAdvertisingOptionFieldsFragment[];
  chamberId: string;
  isCmpInvoiceModuleTestData?: boolean;
  loading: boolean;
  onChanged: () => void;
}) => {
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [editorFieldErrors, setEditorFieldErrors] = useState<EditorFieldErrors>(
    {},
  );
  const [optionPendingDelete, setOptionPendingDelete] =
    useState<CmpAdvertisingOptionFieldsFragment | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const saveInFlightRef = useRef(false);
  const deleteInFlightRef = useRef(false);

  const [createAdvertisingOption, { loading: creating }] =
    useCreateCmpAdvertisingOptionMutation(advertisingOptionMutationOptions);
  const [updateAdvertisingOption, { loading: updating }] =
    useUpdateCmpAdvertisingOptionMutation(advertisingOptionMutationOptions);
  const [deleteAdvertisingOption, { loading: deleting }] =
    useDeleteCmpAdvertisingOptionMutation(advertisingOptionMutationOptions);

  const saving = creating || updating;

  const clearEditorFieldError = (field: keyof EditorFieldErrors) =>
    setEditorFieldErrors((previous) => {
      if (!previous[field]) {
        return previous;
      }

      const next = { ...previous };
      delete next[field];
      return next;
    });

  const handleSave = async () => {
    if (!editor || saveInFlightRef.current) {
      return;
    }

    const priceInCents = parseCurrencyToCents(editor.priceInput);

    // POL-019 (B5): per-field errors replace the section-level-only Alert for
    // the required-field checks.
    const nextFieldErrors: EditorFieldErrors = {};

    // Claude Design AdvertisingOptionModal required-field copy.
    if (!editor.code.trim()) {
      nextFieldErrors.code = "Advertising Code is required.";
    }

    if (!editor.name.trim()) {
      nextFieldErrors.name = "Advertising Description is required.";
    }

    if (!editor.priceInput.trim()) {
      nextFieldErrors.price = "Advertising Cost is required.";
    } else if (priceInCents <= 0) {
      nextFieldErrors.price = "Enter a price greater than $0.00.";
    }

    setEditorFieldErrors(nextFieldErrors);

    if (Object.keys(nextFieldErrors).length > 0) {
      return;
    }

    saveInFlightRef.current = true;
    try {
      setErrorMessage(null);

      if (editor.advertisingOptionId) {
        await updateAdvertisingOption({
          variables: {
            input: {
              chamberId,
              advertisingOptionId: editor.advertisingOptionId,
              code: editor.code.trim(),
              name: editor.name.trim(),
              description: editor.description.trim() || undefined,
              priceInCents,
            },
          },
        });
      } else {
        await createAdvertisingOption({
          variables: {
            input: {
              chamberId,
              code: editor.code.trim(),
              name: editor.name.trim(),
              description: editor.description.trim() || undefined,
              priceInCents,
              isCmpInvoiceModuleTestData:
                isCmpInvoiceModuleTestData || undefined,
            },
          },
        });
      }

      setEditor(null);
      onChanged();
    } catch (error) {
      setErrorMessage(
        getUserFacingErrorText(
          error,
          buildStaffErrorFallback("save the advertising option"),
        ),
      );
    } finally {
      saveInFlightRef.current = false;
    }
  };

  const handleConfirmDelete = async () => {
    if (!optionPendingDelete || deleteInFlightRef.current) {
      return;
    }

    deleteInFlightRef.current = true;
    try {
      setErrorMessage(null);
      await deleteAdvertisingOption({
        variables: {
          input: { chamberId, advertisingOptionId: optionPendingDelete.id },
        },
      });
      setOptionPendingDelete(null);
      onChanged();
    } catch (error) {
      setErrorMessage(
        getUserFacingErrorText(
          error,
          buildStaffErrorFallback("delete the advertising option"),
        ),
      );
    } finally {
      deleteInFlightRef.current = false;
    }
  };

  const closeEditor = () => {
    if (saving || saveInFlightRef.current) {
      return;
    }

    setEditor(null);
    setEditorFieldErrors({});
    setErrorMessage(null);
  };

  const closeDeleteConfirmation = () => {
    if (deleting || deleteInFlightRef.current) {
      return;
    }

    setOptionPendingDelete(null);
    setErrorMessage(null);
  };

  return (
    <InvoiceSection
      label="Advertising"
      title="Advertising Options"
      description="Help promote your member's companies through high impact advertising placements."
      variant="plain"
    >
      <div
        className="overflow-x-auto overflow-y-hidden rounded-[10px] border border-[#ECECEC]"
        role="table"
        aria-label="Advertising options"
      >
        <div className="min-w-[760px]">
          {/* Match the rendered Claude grid exactly. The 16 px grid gaps are
              part of the column geometry and must not be approximated by an
              auto-layout HTML table. */}
          <div
            className="grid grid-cols-[44px_minmax(80px,0.6fr)_minmax(160px,2fr)_minmax(170px,1.1fr)_minmax(170px,1.1fr)] items-center gap-4 bg-[#F7F7F8] text-[12px] font-bold uppercase leading-4 tracking-[0.04em] text-black"
            role="row"
          >
            <div aria-label="Reorder" role="columnheader" />
            <div className="px-1.5 py-[14px]" role="columnheader">
              Code
            </div>
            <div className="px-1.5 py-[14px]" role="columnheader">
              Descriptions
            </div>
            <div className="px-1.5 py-[14px] text-right" role="columnheader">
              Price
            </div>
            <div
              className="sticky right-0 bg-[#F7F7F8] px-4 py-[14px] text-center shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.12)]"
              role="columnheader"
            >
              Actions
            </div>
          </div>

          {loading && advertisingOptions.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-gray-500">
              <CircularProgress size={18} className="mr-2 align-middle" />
              Loading advertising options...
            </div>
          ) : null}
          {!loading && advertisingOptions.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-gray-500">
              No advertising options yet. Add one to begin.
            </div>
          ) : null}
          {advertisingOptions.map((option) => (
            <div
              key={option.id}
              className="grid grid-cols-[44px_minmax(80px,0.6fr)_minmax(160px,2fr)_minmax(170px,1.1fr)_minmax(170px,1.1fr)] items-center gap-4 border-t border-[#ECECEC]"
              role="row"
            >
              <div
                className="flex items-center justify-center text-[#9E9E9E]"
                role="cell"
              >
                <DragIndicatorIcon sx={{ fontSize: 14 }} />
              </div>
              <div
                className="px-1.5 py-4 text-sm font-semibold leading-[18px] text-black"
                role="cell"
              >
                {option.code}
              </div>
              <div
                className="px-1.5 py-4 text-sm leading-[18px] text-black"
                role="cell"
              >
                {option.name}
              </div>
              <div className="px-1.5 py-4 text-right" role="cell">
                <span className="text-[15px] font-bold leading-[19px] text-black">
                  {formatCurrency(option.priceInCents)}
                </span>
                <span className="text-xs font-medium leading-4 text-gray-500">
                  /annually
                </span>
              </div>
              <div
                className="sticky right-0 flex justify-center gap-2 bg-white px-4 py-3 shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.12)]"
                role="cell"
              >
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<EditOutlinedIcon />}
                  onClick={() => {
                    setErrorMessage(null);
                    setEditorFieldErrors({});
                    setEditor(toEditorState(option));
                  }}
                  sx={{
                    backgroundColor: cpaGold,
                    borderRadius: "6px",
                    fontSize: 12,
                    fontWeight: 700,
                    height: 30,
                    lineHeight: "16px",
                    minHeight: 30,
                    minWidth: 0,
                    px: "14px",
                    py: "7px",
                    textTransform: "none",
                    "& .MuiButton-startIcon": {
                      m: 0,
                      mr: "6px",
                      "& svg": { fontSize: "12px" },
                    },
                    "&:hover": { backgroundColor: cpaGoldDark },
                  }}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<DeleteOutlineIcon />}
                  disabled={deleting}
                  onClick={() => {
                    setErrorMessage(null);
                    setOptionPendingDelete(option);
                  }}
                  sx={{
                    backgroundColor: "#F21B1B",
                    borderRadius: "6px",
                    fontSize: 12,
                    fontWeight: 700,
                    height: 30,
                    lineHeight: "16px",
                    minHeight: 30,
                    minWidth: 0,
                    px: "14px",
                    py: "7px",
                    textTransform: "none",
                    "& .MuiButton-startIcon": {
                      m: 0,
                      mr: "6px",
                      "& svg": { fontSize: "12px" },
                    },
                    "&:hover": { backgroundColor: "#D90F1A" },
                  }}
                >
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={() => {
          setErrorMessage(null);
          setEditorFieldErrors({});
          setEditor({ ...emptyEditorState });
        }}
        className="mt-[22px] inline-flex min-h-[40px] items-center rounded-lg px-5 py-[11px] text-sm font-bold leading-[18px] text-white"
        style={{ backgroundColor: cpaGold }}
      >
        + Add Advertising Options
      </button>

      <Dialog
        open={!!editor}
        onClose={closeEditor}
        fullWidth
        maxWidth={false}
        PaperProps={{ sx: invoiceDialogPaperSx({ maxWidth: 700 }) }}
      >
        {editor ? (
          <>
            <DialogTitle
              sx={{ px: { xs: 2.5, sm: 3.5 }, pb: 0, pt: { xs: 2.5, sm: 3.5 } }}
            >
              <InvoiceDialogCloseButton
                ariaLabel="Close advertising option editor"
                disabled={saving}
                onClick={closeEditor}
              />
              <span className="block pr-10 text-[21px] font-bold leading-tight text-black">
                {editor.advertisingOptionId
                  ? "Edit Advertising Item"
                  : "Create Advertising Item"}
              </span>
              <span className="mt-1 block text-sm font-normal text-black">
                To be used for Invoicing
              </span>
            </DialogTitle>

            <DialogContent
              sx={{ px: { xs: 2.5, sm: 3.5 }, py: { xs: 1.75, sm: 2.25 } }}
            >
              {errorMessage ? (
                <Alert
                  className="mb-4"
                  onClose={() => setErrorMessage(null)}
                  severity="error"
                >
                  {errorMessage}
                </Alert>
              ) : null}
              <div className="flex flex-col gap-3.5">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[15px] font-bold text-black">
                    Advertising Code <span className="text-[#E24C4C]">*</span>
                  </label>
                  <p className="text-sm text-black">
                    Enter the code specific for advertising description
                  </p>
                  <TextField
                    placeholder="Enter Code"
                    value={editor.code}
                    sx={{ ...invoiceFieldSx, maxWidth: 300 }}
                    error={Boolean(editorFieldErrors.code)}
                    helperText={editorFieldErrors.code}
                    onChange={({ target }) => {
                      clearEditorFieldError("code");
                      setEditor({ ...editor, code: target.value });
                    }}
                    fullWidth
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[15px] font-bold text-black">
                    Advertising Description{" "}
                    <span className="text-[#E24C4C]">*</span>
                  </label>
                  <p className="text-sm text-black">
                    Enter the description which will be seen on the Invoice.
                  </p>
                  <TextField
                    placeholder="Enter Advertising Description"
                    value={editor.name}
                    sx={invoiceFieldSx}
                    error={Boolean(editorFieldErrors.name)}
                    helperText={editorFieldErrors.name}
                    onChange={({ target }) => {
                      clearEditorFieldError("name");
                      setEditor({
                        ...editor,
                        name: target.value.slice(0, DESCRIPTION_MAX_LENGTH),
                      });
                    }}
                    fullWidth
                  />
                  <p className="text-xs text-[#9E9E9E]">
                    {editor.name.length}/{DESCRIPTION_MAX_LENGTH} characters
                  </p>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[15px] font-bold text-black">
                    Advertising Cost <span className="text-[#E24C4C]">*</span>
                  </label>
                  {/* POL-005 (B5): decimal text input (no spinners), "$" prefix
                    kept, blur-canonicalized to 2 decimals; the dollars-string
                    state stays the storage boundary (cents at save). */}
                  <TextField
                    placeholder="$0.00"
                    value={editor.priceInput}
                    sx={invoiceFieldSx}
                    error={Boolean(editorFieldErrors.price)}
                    helperText={editorFieldErrors.price}
                    inputProps={{ inputMode: "decimal" }}
                    onChange={({ target }) => {
                      clearEditorFieldError("price");
                      setEditor({
                        ...editor,
                        priceInput: sanitizeCurrencyValue(target.value),
                      });
                    }}
                    onBlur={() =>
                      setEditor((previous) =>
                        previous
                          ? {
                              ...previous,
                              priceInput: formatCurrencyValueOnBlur(
                                previous.priceInput,
                              ),
                            }
                          : previous,
                      )
                    }
                    // Design AdvertisingOptionModal.jsx:94: the empty field
                    // shows the "$0.00" placeholder; a value keeps its "$".
                    InputProps={
                      editor.priceInput
                        ? {
                            startAdornment: (
                              <InputAdornment position="start">$</InputAdornment>
                            ),
                          }
                        : undefined
                    }
                    fullWidth
                  />
                </div>
              </div>
            </DialogContent>

            <DialogActions
              sx={{
                justifyContent: "flex-start",
                px: { xs: 2.5, sm: 3.5 },
                pb: { xs: 2.5, sm: 3.5 },
                pt: 0,
              }}
            >
              <Button
                variant="contained"
                disabled={saving}
                onClick={() => void handleSave()}
                startIcon={
                  saving ? (
                    <CircularProgress color="inherit" size={18} />
                  ) : undefined
                }
                sx={invoiceModalActionSx({
                  backgroundColor: cpaGold,
                  hoverBackgroundColor: cpaGoldDark,
                })}
              >
                Save Advertising Option
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      <Dialog
        open={!!optionPendingDelete}
        onClose={closeDeleteConfirmation}
        fullWidth
        maxWidth={false}
        PaperProps={{ sx: invoiceDialogPaperSx({ maxWidth: 440 }) }}
      >
        {optionPendingDelete ? (
          <>
            <DialogTitle sx={{ px: 3.5, pb: 0, pt: 3.75, textAlign: "center" }}>
              <InvoiceDialogCloseButton
                ariaLabel="Close delete confirmation"
                disabled={deleting}
                onClick={closeDeleteConfirmation}
              />
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#F21B1B] text-white">
                <DeleteOutlineIcon sx={{ fontSize: 30 }} />
              </div>
              <span className="mt-[18px] block text-sm font-semibold text-black">
                Delete Advertising Option
              </span>
            </DialogTitle>
            <DialogContent sx={{ px: 3.5, pb: 0, pt: 1 }}>
              <p className="mx-auto max-w-[340px] text-center text-sm leading-[1.55] text-black">
                Are you sure you want to delete the advertising option{" "}
                {optionPendingDelete.name}?
              </p>
              {errorMessage ? (
                <Alert
                  className="mt-4"
                  onClose={() => setErrorMessage(null)}
                  severity="error"
                >
                  {errorMessage}
                </Alert>
              ) : null}
            </DialogContent>
            <DialogActions
              sx={{
                display: "flex",
                gap: 1.5,
                justifyContent: "center",
                px: 3.5,
                pb: 3.75,
                pt: 2.75,
                width: "100%",
                "& > :not(style) ~ :not(style)": {
                  marginLeft: 0,
                },
              }}
            >
              <Button
                variant="contained"
                disabled={deleting}
                startIcon={<DeleteOutlineIcon sx={{ fontSize: 30 }} />}
                onClick={() => void handleConfirmDelete()}
                sx={[
                  invoiceModalActionSx({
                    backgroundColor: "#F21B1B",
                    hoverBackgroundColor: "#D90F1A",
                  }),
                  { flex: "1 1 0", maxWidth: 170 },
                ]}
              >
                Delete
              </Button>
              <Button
                variant="contained"
                disabled={deleting}
                onClick={closeDeleteConfirmation}
                sx={[
                  invoiceModalActionSx({
                    backgroundColor: "#26282C",
                    hoverBackgroundColor: "#111827",
                  }),
                  { flex: "1 1 0", maxWidth: 170 },
                ]}
              >
                Cancel
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>
    </InvoiceSection>
  );
};
